/**
 * El selector de empresa — `OD-0060` D-04. Para quien está en más de una: con
 * una sola no se pinta nada.
 *
 * La lista y el cambio los da la API con la sesión y nada más, así que también
 * funciona desde la pantalla de quien se ha quedado fuera de la suya. Al
 * cambiar no se toca nada de aquí: quien lo usa decide qué recargar
 * (`onCambiada`), porque todo lo que hay en pantalla es de la empresa anterior.
 *
 * Un 404 al cambiar es lo normal si, desde que se cargó la lista, a esa
 * persona le han quitado o desactivado en la otra empresa: se dice así, sin
 * el «no se ha encontrado» genérico.
 *
 * El cambio lo hace `useCambioDeEmpresa`, que antes pregunta si hay otra
 * pestaña abierta (`OD-0063` D-05): la empresa activa es de la sesión, y la
 * otra pestaña pasaría a esta sin saberlo.
 */
import { useEffect, useState } from "react";
import { apiEmpresas, type EmpresaDeLaSesion } from "../api/cliente";
import { t } from "../i18n/es";
import { Aviso, Boton, Selector } from "../ui/primitivas";
import { useCambioDeEmpresa } from "./CambioDeEmpresa";

export function SelectorDeEmpresa({
  id,
  onCambiada,
  onSesionCaducada,
}: {
  /** Prefijo de los ids del formulario: puede haber dos selectores en la misma página. */
  id: string;
  onCambiada: () => void;
  onSesionCaducada: () => void;
}) {
  const [empresas, setEmpresas] = useState<readonly EmpresaDeLaSesion[]>([]);
  const [elegida, setElegida] = useState<string>("");
  const cambio = useCambioDeEmpresa(onCambiada, onSesionCaducada);

  useEffect(() => {
    let vigente = true;
    void apiEmpresas.listar().then((r) => {
      if (!vigente || !r.ok) return;
      setEmpresas(r.datos);
      setElegida(r.datos.find((e) => e.activa)?.id ?? r.datos[0]?.id ?? "");
    });
    return () => {
      vigente = false;
    };
  }, []);

  if (empresas.length < 2) return null;
  const activa = empresas.find((e) => e.activa)?.id ?? null;

  return (
    <div className="selector-empresa">
      <Selector
        id={`${id}-empresa`}
        etiqueta={t.selectorEmpresa.etiqueta}
        valor={elegida}
        opciones={empresas.map((e) => ({ valor: e.id, texto: e.nombre }))}
        onChange={setElegida}
        deshabilitado={cambio.cambiando}
      />
      {cambio.error !== null ? <Aviso tono="error">{cambio.error}</Aviso> : null}
      {cambio.confirmacion}
      {elegida !== activa && cambio.confirmacion === null ? (
        <Boton variante="secundario" onClick={() => void cambio.cambiar(elegida)} deshabilitado={cambio.cambiando}>
          {cambio.cambiando ? t.selectorEmpresa.cambiando : t.selectorEmpresa.cambiar}
        </Boton>
      ) : null}
    </div>
  );
}
